//! «Сегодня» — компактный виджет наверху дашборда. Показывает то, что
//! горит прямо сейчас:
//!   - Ближайшие 5 сроков (любых: договоры, претензии, корреспонденция, gov)
//!   - Свежие документы (договоры + претензии + иски, последние ~7 дней)
//!   - Договоры под анализом LLM (live-индикатор)
//!   - Запросы госорганов с истекающим сроком (high priority + ≤3 дня)
//!
//! Кликабельные строки — переход в соответствующий раздел или карточку.

import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { Skeleton } from '@shared/ui/Skeleton';
import {
  useListClaimsQuery,
  useListContractsQuery,
  useListDeadlinesQuery,
  useListGovQuery,
} from '@shared/api';
import { govAuthorityLabel } from '@shared/lib/statusLabels';

const Wrap = styled.section`
  margin-bottom: ${({ theme }) => theme.spacing(5)};
  padding: ${({ theme }) => theme.spacing(4)};
  background: ${({ theme }) => theme.colors.backgroundElevated};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.lg};
`;

const Head = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  margin-bottom: ${({ theme }) => theme.spacing(3)};

  & > h3 {
    margin: 0;
    font-size: 14px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    color: ${({ theme }) => theme.colors.text.muted};
  }

  & > .date {
    font-size: 12px;
    color: ${({ theme }) => theme.colors.text.muted};
  }
`;

const Cols = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: ${({ theme }) => theme.spacing(4)};
`;

const Col = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(2)};
`;

const ColTitle = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.secondary};
  display: flex;
  align-items: center;
  gap: 6px;
`;

const Dot = styled.span<{ $tone: 'danger' | 'warning' | 'success' | 'info' }>`
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${({ $tone, theme }) =>
    $tone === 'danger'
      ? theme.colors.status.danger
      : $tone === 'warning'
        ? theme.colors.status.warning
        : $tone === 'success'
          ? theme.colors.status.success
          : theme.colors.primary};
`;

const Row = styled.button`
  text-align: left;
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid transparent;
  border-radius: ${({ theme }) => theme.radius.md};
  padding: ${({ theme }) => theme.spacing(2.5)} ${({ theme }) => theme.spacing(3)};
  cursor: pointer;
  font: inherit;
  color: inherit;
  transition:
    border-color 120ms ease,
    transform 120ms ease;

  &:hover {
    border-color: ${({ theme }) => theme.colors.primary};
    transform: translateX(2px);
  }
`;

const RowMain = styled.div`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.primary};
  display: flex;
  justify-content: space-between;
  gap: 8px;
  align-items: baseline;
`;

const RowSub = styled.div`
  font-size: 11px;
  color: ${({ theme }) => theme.colors.text.muted};
  margin-top: 2px;
`;

const Tag = styled.span<{ $tone: 'danger' | 'warning' | 'success' | 'info' }>`
  flex-shrink: 0;
  font-size: 11px;
  padding: 2px 8px;
  border-radius: 999px;
  font-weight: 500;
  background: ${({ $tone, theme }) => {
    if ($tone === 'danger') return `${theme.colors.status.danger}22`;
    if ($tone === 'warning') return `${theme.colors.status.warning}22`;
    if ($tone === 'success') return `${theme.colors.status.success}22`;
    return `${theme.colors.primary}22`;
  }};
  color: ${({ $tone, theme }) => {
    if ($tone === 'danger') return theme.colors.status.danger;
    if ($tone === 'warning') return theme.colors.status.warning;
    if ($tone === 'success') return theme.colors.status.success;
    return theme.colors.primary;
  }};
`;

const Empty = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.muted};
  font-style: italic;
  padding: ${({ theme }) => theme.spacing(2)} 0;
`;

const SCOPE_LABELS: Record<string, string> = {
  contract: 'Договор',
  claim: 'Претензия',
  correspondence: 'Письмо',
  gov: 'Госорган',
};

const KIND_LABELS: Record<string, string> = {
  claim_response: 'ответ на претензию',
  work_start: 'начало работ',
  work_end: 'окончание работ',
  warranty: 'гарантия',
  prolong: 'пролонгация',
  force_majeure_notice: 'форс-мажор',
  payment: 'оплата',
  procedural: 'процессуальный',
  answer: 'ответ',
  statute_of_limitations: 'исковая давность',
};

function daysUntil(iso: string): number {
  return Math.round((new Date(iso).getTime() - Date.now()) / 86_400_000);
}

function daysLabel(d: number): {
  text: string;
  tone: 'danger' | 'warning' | 'success';
} {
  if (d < 0) return { text: `просрочено на ${-d} дн.`, tone: 'danger' };
  if (d === 0) return { text: 'сегодня', tone: 'danger' };
  if (d === 1) return { text: 'завтра', tone: 'warning' };
  if (d <= 3) return { text: `${d} дн.`, tone: 'warning' };
  return { text: `${d} дн.`, tone: 'success' };
}

export function TodayWidget() {
  const navigate = useNavigate();
  const todayStr = new Date().toLocaleDateString('ru-RU', {
    day: 'numeric',
    month: 'long',
    weekday: 'long',
  });

  // Все запросы стартуют параллельно — отдельные тайлы не блокируют друг друга.
  const { data: deadlines, isLoading: lDl } = useListDeadlinesQuery({
    within_days: 14,
    limit: 5,
  });
  // «Свежие документы для проверки» — последние 5 договоров и претензий,
  // отсортированные по дате создания. То что юристу стоит просмотреть в
  // первую очередь: новый договор от контрагента, входящая претензия,
  // только что классифицированный иск.
  const { data: freshContracts, isLoading: lFreshC } = useListContractsQuery({
    sort: 'created_at',
    order: 'desc',
    limit: 3,
  });
  const { data: freshClaims, isLoading: lFreshCl } = useListClaimsQuery({
    sort: 'created_at',
    order: 'desc',
    limit: 3,
  });
  const { data: contracts, isLoading: lContracts } = useListContractsQuery({
    status: 'under_review',
    sort: 'created_at',
    order: 'desc',
    limit: 3,
  });
  const { data: gov, isLoading: lGov } = useListGovQuery({
    status: 'new',
    sort: 'deadline',
    order: 'asc',
    limit: 3,
  });

  const dlList = deadlines?.items ?? [];
  const contractsList = contracts?.items ?? [];
  const govList = gov?.items ?? [];

  // Сливаем свежие договоры и претензии в один список по дате. Берём
  // только последние 7 дней — иначе виджет начинает показывать историю,
  // а смысл «Сегодня» — что новое.
  const sevenDaysAgo = Date.now() - 7 * 86_400_000;
  const lFresh = lFreshC || lFreshCl;
  type FreshItem =
    | { kind: 'contract'; id: string; title: string; subtitle: string; created_at: string }
    | { kind: 'claim'; id: string; title: string; subtitle: string; created_at: string; claim_type: string };
  const freshList: FreshItem[] = [
    ...(freshContracts?.items ?? []).map((c) => ({
      kind: 'contract' as const,
      id: c.id,
      title: c.counterparty,
      subtitle: c.subject ?? 'договор',
      created_at: c.created_at,
    })),
    ...(freshClaims?.items ?? []).map((c) => ({
      kind: 'claim' as const,
      id: c.id,
      title: c.counterparty,
      subtitle:
        (c.claim_type === 'lawsuit' ? 'иск' : 'претензия') +
        (c.dispute_type ? ` · ${c.dispute_type}` : ''),
      created_at: c.created_at,
      claim_type: c.claim_type,
    })),
  ]
    .filter((it) => new Date(it.created_at).getTime() >= sevenDaysAgo)
    .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
    .slice(0, 5);

  // Если всё пусто — показываем компактное сообщение «всё в порядке».
  const totallyEmpty =
    !lDl &&
    !lFresh &&
    !lContracts &&
    !lGov &&
    dlList.length === 0 &&
    freshList.length === 0 &&
    contractsList.length === 0 &&
    govList.length === 0;

  if (totallyEmpty) {
    return (
      <Wrap>
        <Head>
          <h3>Сегодня</h3>
          <span className="date">{todayStr}</span>
        </Head>
        <Empty>
          Срочных задач нет: дедлайнов в ближайшие 14 дней, новых договоров/претензий и
          активных анализов нет. Можно заниматься плановой работой.
        </Empty>
      </Wrap>
    );
  }

  return (
    <Wrap>
      <Head>
        <h3>Сегодня</h3>
        <span className="date">{todayStr}</span>
      </Head>
      <Cols>
        {/* Колонка 1 — Сроки */}
        <Col>
          <ColTitle>
            <Dot $tone="warning" />
            Ближайшие сроки
          </ColTitle>
          {lDl ? (
            <>
              <Skeleton $height={48} />
              <Skeleton $height={48} />
              <Skeleton $height={48} />
            </>
          ) : dlList.length === 0 ? (
            <Empty>Активных сроков нет.</Empty>
          ) : (
            dlList.map((d) => {
              const days = daysUntil(d.due_at);
              const label = daysLabel(days);
              const dt = new Date(d.due_at).toLocaleDateString('ru-RU');
              return (
                <Row
                  key={d.id}
                  type="button"
                  onClick={() => navigate('/agents/statut/deadlines')}
                  title="Открыть реестр сроков"
                >
                  <RowMain>
                    <span>
                      <strong>{SCOPE_LABELS[d.scope] ?? d.scope}</strong> ·{' '}
                      {KIND_LABELS[d.kind] ?? d.kind}
                    </span>
                    <Tag $tone={label.tone}>{label.text}</Tag>
                  </RowMain>
                  <RowSub>
                    {dt}
                    {d.note && ` · ${d.note}`}
                  </RowSub>
                </Row>
              );
            })
          )}
        </Col>

        {/* Колонка 2 — Свежие договоры/претензии/иски за последние 7 дней */}
        <Col>
          <ColTitle>
            <Dot $tone="info" />
            Новые документы
          </ColTitle>
          {lFresh ? (
            <>
              <Skeleton $height={48} />
              <Skeleton $height={48} />
            </>
          ) : freshList.length === 0 ? (
            <Empty>Новых договоров, претензий и исков за неделю нет.</Empty>
          ) : (
            freshList.map((it) => {
              const tagLabel =
                it.kind === 'contract'
                  ? 'договор'
                  : it.kind === 'claim' && (it as { claim_type: string }).claim_type === 'lawsuit'
                    ? 'иск'
                    : 'претензия';
              const route =
                it.kind === 'contract' ? '/agents/statut/contracts' : '/agents/statut/claims';
              return (
                <Row
                  key={`${it.kind}-${it.id}`}
                  type="button"
                  onClick={() => navigate(route)}
                  title={it.kind === 'contract' ? 'Открыть договоры' : 'Открыть претензии/иски'}
                >
                  <RowMain>
                    <span>{it.title}</span>
                    <Tag $tone="info">{tagLabel}</Tag>
                  </RowMain>
                  <RowSub>
                    {it.subtitle} ·{' '}
                    {new Date(it.created_at).toLocaleString('ru-RU', {
                      hour: '2-digit',
                      minute: '2-digit',
                      day: '2-digit',
                      month: '2-digit',
                    })}
                  </RowSub>
                </Row>
              );
            })
          )}
        </Col>

        {/* Колонка 3 — Договоры под анализом + горячие запросы госорганов */}
        <Col>
          <ColTitle>
            <Dot $tone="success" />В работе у LLM
          </ColTitle>
          {lContracts ? (
            <>
              <Skeleton $height={48} />
              <Skeleton $height={48} />
            </>
          ) : contractsList.length === 0 ? (
            <Empty>Активных анализов нет.</Empty>
          ) : (
            contractsList.map((c) => (
              <Row
                key={c.id}
                type="button"
                onClick={() => navigate('/agents/statut/contracts')}
                title="Открыть договоры"
              >
                <RowMain>
                  <span>{c.counterparty}</span>
                  <Tag $tone="success">анализ</Tag>
                </RowMain>
                <RowSub>{c.subject ?? 'предмет не указан'}</RowSub>
              </Row>
            ))
          )}

          {lGov ? null : govList.length === 0 ? null : (
            <>
              <ColTitle style={{ marginTop: 8 }}>
                <Dot $tone="danger" />
                Запросы от госорганов
              </ColTitle>
              {govList.map((g) => {
                const days = g.deadline != null ? daysUntil(g.deadline) : null;
                const label = days != null ? daysLabel(days) : null;
                return (
                  <Row
                    key={g.id}
                    type="button"
                    onClick={() => navigate('/agents/statut/gov')}
                    title="Открыть запросы госорганов"
                  >
                    <RowMain>
                      <span>{g.subject}</span>
                      {label && <Tag $tone={label.tone}>{label.text}</Tag>}
                    </RowMain>
                    <RowSub>
                      {govAuthorityLabel(g.authority)}
                      {g.priority === 'high' && ' · высокий приоритет'}
                    </RowSub>
                  </Row>
                );
              })}
            </>
          )}
        </Col>
      </Cols>
    </Wrap>
  );
}
